import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSuppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    api_url: '',
    api_key: '',
    contact_email: '',
    notes: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/admin/suppliers`);
      setSuppliers(response.data.suppliers);
    } catch (error) {
      toast.error('Errore nel caricamento fornitori');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editSupplier) {
        await axios.put(`${API}/admin/suppliers/${editSupplier.id}`, formData);
        toast.success('Fornitore aggiornato');
      } else {
        await axios.post(`${API}/admin/suppliers`, formData);
        toast.success('Fornitore creato');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo fornitore?')) return;
    
    try {
      await axios.delete(`${API}/admin/suppliers/${supplierId}`);
      toast.success('Fornitore eliminato');
      fetchSuppliers();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const openEditDialog = (supplier) => {
    setEditSupplier(supplier);
    setFormData({
      name: supplier.name,
      api_url: supplier.api_url || '',
      api_key: supplier.api_key || '',
      contact_email: supplier.contact_email || '',
      notes: supplier.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditSupplier(null);
    setFormData({
      name: '',
      api_url: '',
      api_key: '',
      contact_email: '',
      notes: ''
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-suppliers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-serif">Fornitori</h1>
          <p className="text-muted-foreground mt-1">Gestisci i tuoi fornitori e grossisti</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-supplier-btn">
          <Plus className="h-4 w-4 mr-2" />
          Aggiungi Fornitore
        </Button>
      </div>

      {/* Suppliers Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-border">
          <p className="text-muted-foreground mb-4">Nessun fornitore configurato</p>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi il primo fornitore
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {suppliers.map((supplier) => (
            <div
              key={supplier.id}
              className="bg-white rounded-xl border border-border p-6"
              data-testid={`supplier-card-${supplier.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">{supplier.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    supplier.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {supplier.is_active ? 'Attivo' : 'Disattivato'}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(supplier)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(supplier.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {supplier.contact_email && (
                <p className="text-sm text-muted-foreground mb-2">
                  {supplier.contact_email}
                </p>
              )}
              {supplier.api_url && (
                <p className="text-xs text-muted-foreground font-mono truncate">
                  API: {supplier.api_url}
                </p>
              )}
              {supplier.notes && (
                <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                  {supplier.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editSupplier ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Fornitore *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                data-testid="supplier-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact_email">Email Contatto</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="api_url">URL API (opzionale)</Label>
              <Input
                id="api_url"
                value={formData.api_url}
                onChange={(e) => setFormData(prev => ({ ...prev, api_url: e.target.value }))}
                placeholder="https://api.fornitore.com/products"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="api_key">API Key (opzionale)</Label>
              <Input
                id="api_key"
                type="password"
                value={formData.api_key}
                onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Note</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-supplier-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSuppliers;
